import { Observable } from 'rxjs/Observable';
import { ActionOptionsArgs } from '../../../core/store/actions/base-actions';
import { isInArray } from '../../../core/util/is-in-array';
import { intersect, mergeDedupe } from '../../../core/util/utils';
import { Control } from '../control/control.model';
import { Form } from '../form/form.model';
import { Qry } from '../query-facade';
import { RestrictedAccountObjectStoreFacade } from '../restricted-account-object/restricted-account-object.store-facade';
import * as actions from './group.actions';
import { Group } from './group.model';
import { removeFromArray } from '../../../core/util/remove-from-array';
import { arrayPushUnique } from '../../../core/util/array-push-unique';

export class GroupStoreFacade extends RestrictedAccountObjectStoreFacade<Group> {

    getAll(): Observable<Array<Group>> {
        return this.select(Qry.getInstance().group.byActiveAccount);
    }

    create(group: Group): void {
        this.submit(group);
    }

    update(group: Group): void {
        this.submit(group);
    }

    insert(group: Group): void {
        this.dispatch(new actions.SubmitAction(group, {persistable: true, restful: false}));
    }

    remove(group: Group, options?: ActionOptionsArgs): void {

        // detach the group from any forms that reference it
        this.appFacade.objects.form.store
            .getBy(form => isInArray(group.id, form.groups))
            .take(1)
            .subscribe(forms => {
                forms.forEach(form => {
                    this.appFacade.objects.form.store.detachGroup(form, group);
                });
            });

        // remove any controls that are referenced by the group
        this.appFacade.objects.control.store
            .getBy(control => control.group === group.id)
            .take(1)
            .subscribe(controls => {
                controls.forEach(control => {
                    this.appFacade.objects.control.store.remove(control, options);
                });
            });

        this.dispatch(new actions.RemoveAction(group, options));
    }

    attachForm(group: Group, form: Form): void {

        if (!group || !form) {
            return;
        }

        if (isInArray(form.id, group.forms)) {
            return;
        }

        this.dispatch(new actions.SubmitAction(new Group({
            ...group,
            forms: arrayPushUnique(group.forms as Array<string>, form.id)
        })));
    }

    detachForm(group: Group, form: Form): void {

        if (!group || !form) {
            return;
        }

        if (!isInArray(form.id, group.forms)) {
            return;
        }

        this.dispatch(new actions.SubmitAction(new Group({
            ...group,
            forms: removeFromArray(group.forms as Array<string>, form.id)
        })));
    }

    attachControl(group: Group, control: Control): void {

        if (isInArray(control.id, group.controls)) {
            return;
        }

        this.dispatch(new actions.SubmitAction(new Group({
            ...group,
            controls: arrayPushUnique(group.controls, control.id)
        })));
    }

    detachControl(group: Group, control: Control): void {

        if (!isInArray(control.id, group.controls)) {
            return;
        }

        this.dispatch(new actions.SubmitAction(new Group({
            ...group,
            controls: removeFromArray(group.controls, control.id)
        })));
    }

    protected submit(group: Group): void {

        let forms: Array<Form> = [];
        let controls: Array<Control> = [];

        this.appFacade.objects.form.store.getAll().take(1).subscribe(_forms => forms = _forms);
        this.appFacade.objects.control.store.getAll().take(1).subscribe(_controls => controls = _controls);

        const formIds = forms.map(form => form.id);
        const formIdIntersection = intersect(group.forms, formIds);

        // ensure the group does not contain any form ids that do not exist (yet)
        this.dispatch(new actions.SubmitAction(new Group({
            ...group,
            forms: formIdIntersection
        })));

        // make sure the id of this Group is added to each Form it references
        group.forms.forEach(formId => {
            const form = forms.find(f => f.id === formId);
            this.appFacade.objects.form.store.attachGroup(form, group);
        });

        // make sure the id of this Group is added to each Control it references
        controls
            .filter(control => isInArray(control.id, group.controls))
            .forEach(control => this.appFacade.objects.control.store.setGroup(control, group));
    }
}



// WEBPACK FOOTER //
// ./src/vas/business-objects/group/group.store-facade.ts